import { ImageName } from "@mcleod/images";
import { Button, ButtonVariant, Component, ComponentFactory, Label, LabelProps, Panel, Layout } from "@mcleod/components";
import { PermissionsDesignerTab } from "./PermissionsDesignerTab";
import { Alignment, StringUtil } from "@mcleod/core";

export class PermissionsTabTooltip extends Panel {
    private designerTab: PermissionsDesignerTab;

    constructor(designerTab: PermissionsDesignerTab) {
        super();
        this.designerTab = designerTab;
        this.generateTooltipContent();
    }

    private generateTooltipContent() {
        let nestedLayoutLabelAdded = false;

        const directComps = Array.from(this.designerTab.permedComponentMap?.get(this.designerTab.path) ?? []);
        const explicitlyPermedDirectComps = directComps.filter(comp => {
            if (comp instanceof Layout && comp.isNested === true) {
                return this.designerTab.designer.permissionsSource
                    .getFieldLevelSecureItemRows(this.designerTab.path)
                    .some(row => row.get("item_name") === comp.id);
            }
            return true;
        });

        if (explicitlyPermedDirectComps.length > 0) {
            this.addDirectPermissionsContent(this.getBulletListLabelProps(this.designerTab.path, new Set(explicitlyPermedDirectComps)));
        }

        this.designerTab.nestedLayoutPaths.forEach(nestedPath => {
            const innerComps = this.designerTab.permedComponentMap?.get(nestedPath) ?? new Set();
            const bulletProps = this.getBulletListLabelProps(nestedPath, innerComps);

            if (this.designerTab.designer.permissionsSource.hasTopLevelSecureItemRows(nestedPath)) {
                bulletProps.unshift({
                    text: "Entire Page",
                    tooltip: "Open for editing field-level permissions",
                    tooltipPosition: Alignment.RIGHT,
                    color: "primary",
                    paddingLeft: 16,
                    onClick: () => this.designerTab.designer.openTab({ path: nestedPath })
                });
            }

            if (bulletProps.length > 0) {
                if (!nestedLayoutLabelAdded) {
                    nestedLayoutLabelAdded = true;
                    this.addNestedLayoutsLabel();
                }
                this.addNestedLayoutPanel(nestedPath, bulletProps);
            }
        });
    }

    private addDirectPermissionsContent(bulletPointLabelProps: Partial<LabelProps>[]) {
        this.add(new Label({
            text: "Permissions set directly in this layout",
            paddingTop: 8,
            fontBold: true
        }));
        this.add(...ComponentFactory.createBulletedList(bulletPointLabelProps));
    }

    private addNestedLayoutsLabel() {
        this.add(new Label({
            text: "Permissions set in nested layouts",
            paddingTop: 12,
            fontBold: true
        }));
    }

    private addNestedLayoutPanel(path: string, bulletPointLabelProps: Partial<LabelProps>[]) {
        const nestedLayoutPanel = new Panel({ padding: 0, paddingRight: 16 });

        nestedLayoutPanel.add(new Label({
            text: `${StringUtil.stringAfterLast(path, "/")} Layout`,
            paddingTop: 8,
            paddingLeft: 16,
            rowBreak: false,
        }));

        const button = new Button({
            id: "buttonDetail",
            margin: 0,
            imageName: ImageName.arrowGoTo,
            tooltip: "Open for editing field-level permissions",
            variant: ButtonVariant.round,
            onClick: () => this.designerTab.designer.openTab({ path })
        });
        nestedLayoutPanel.add(button);

        nestedLayoutPanel.add(...ComponentFactory.createBulletedList(bulletPointLabelProps));

        this.add(nestedLayoutPanel);
    }

    private getBulletListLabelProps(path: string, permedComps: Set<Component>): Partial<LabelProps>[] {
        if (!permedComps) return [];

        return Array.from(permedComps).map(component => ({
            text: this.designerTab.isMainLayout(component) ? "Entire Page" : component.id,
            tooltip: "Scroll to component",
            tooltipPosition: Alignment.RIGHT,
            color: "primary",
            paddingLeft: 16,
            onClick: () => {
                this.designerTab.selectAndScrollToComponent(path, component);
            }
        }));
    }
}
